import * as React from 'react';
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '28px 12px',
    textAlign: 'center',
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    margin: '4px 0 0',
    padding: 0,
    listStyle: 'none',
    textAlign: 'left',
  },
  feature: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    gap: '8px',
  },
  upgradeBtn: {
    minWidth: 'auto',
    height: '36px',
    borderRadius: '10px',
    padding: '0 20px',
    fontWeight: tokens.fontWeightSemibold,
    color: '#ffffff',
    border: 'none',
    background: 'linear-gradient(90deg, #8B2FE0 0%, #DC00E4 50%, #FF7700 100%)',
    ':hover': {
      color: '#ffffff',
      opacity: 0.92,
    },
    ':hover:active': {
      color: '#ffffff',
      opacity: 0.85,
    },
  },
  footnote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

/**
 * Two different things route here and they are not the same message:
 *
 * - `quota` — the team is on a paid plan and burned through a metered feature's allowance
 *   this billing period. "You hit your limit" is accurate.
 * - `plan` — the team is on FREE, where the Word add-in is not included at all. The user has
 *   used nothing, so limit language is simply wrong, and this is the screen an AppSource
 *   reviewer lands on with their free-tier test account. It has to explain what a subscription
 *   unlocks rather than read like a failure state.
 */
export type UpgradeVariant = 'quota' | 'plan';

const PLAN_FEATURES = [
  'Risk analysis and document summaries',
  'AI redlining with tracked changes',
  'Full-document review',
  'Agreement drafting from templates',
  'Sigi chat about your document',
];

interface UpgradePromptProps {
  variant?: UpgradeVariant;
  /** Overrides the variant's default body copy. */
  message?: string;
  onUpgrade: () => void;
  loading?: boolean;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  variant = 'quota',
  message,
  onUpgrade,
  loading,
}) => {
  const styles = useStyles();
  const isPlanGate = variant === 'plan';

  const title = isPlanGate ? 'Signus for Word is a paid feature' : 'You have reached your limit';
  const body =
    message ??
    (isPlanGate
      ? 'Your team is on the Free plan, which does not include the Word add-in. Upgrade to unlock:'
      : "Looks like you hit your plan's AI usage limit. Please upgrade to continue.");

  return (
    <div className={styles.root}>
      <Text className={styles.title}>{title}</Text>
      <Text className={styles.message}>{body}</Text>

      {isPlanGate && (
        <ul className={styles.featureList}>
          {PLAN_FEATURES.map(feature => (
            <li key={feature} className={styles.feature}>
              <span aria-hidden="true">✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      <Button className={styles.upgradeBtn} onClick={onUpgrade} disabled={loading}>
        {loading ? 'Opening…' : 'Upgrade'}
      </Button>

      {isPlanGate && (
        <Text className={styles.footnote}>Opens your team billing page in the browser.</Text>
      )}
    </div>
  );
};

export default UpgradePrompt;
